import React, { useState, useRef, useEffect, useId } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  Sun,
  Moon,
  LogOut,
  User,
  Coins,
  ChevronDown,
  Check,
  HelpCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PANEL_BASE, panelPath } from '../config/paths';
import { SUPPORTED_LOCALES, type LocaleCode } from '../config/i18n';
import BrandLogo from './BrandLogo';

const LOCALE_FLAGS: Record<LocaleCode, string> = {
  tr: '🇹🇷',
  en: '🇬🇧',
};

const PLAN_LABEL_KEY: Record<string, string> = {
  starter: 'header.planStarter',
  pro: 'header.planPro',
  enterprise: 'header.planEnterprise',
  goodmode: 'header.planGoodmode',
};

interface HeaderProps {
  onMenuClick: () => void;
  onThemeToggle: () => void;
  darkMode: boolean;
}

function getInitials(email: string): string {
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._\-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase() || '?';
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, onThemeToggle, darkMode }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const currentLang = (i18n.language?.split('-')[0] || 'en') as LocaleCode;
  const effectiveLang = SUPPORTED_LOCALES.includes(currentLang) ? currentLang : 'en';

  const setLanguage = (code: LocaleCode) => {
    i18n.changeLanguage(code);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  const closeAndNavigate = () => setMenuOpen(false);

  const handleRestartOnboarding = () => {
    setMenuOpen(false);
    window.dispatchEvent(new CustomEvent('buildmate:restart-onboarding'));
  };

  const planKey = user?.plan ? PLAN_LABEL_KEY[user.plan] : undefined;
  const planLabel = planKey ? t(planKey) : t('header.planStarter');
  const creditBalance = (user?.aiCredits?.balance ?? 0).toLocaleString();

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200/80 dark:border-gray-700/80 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md supports-[backdrop-filter]:bg-white/75 dark:supports-[backdrop-filter]:bg-gray-800/75 px-3 sm:px-4 h-14 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onMenuClick}
          className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors"
          aria-label={t('header.toggleSidebar')}
        >
          <Menu className="h-5 w-5" />
        </button>

        <Link to={PANEL_BASE} className="flex items-center gap-2 min-w-0 hover:opacity-90">
          <BrandLogo className="h-7 w-7 rounded-md" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {t('common.appName')}
          </h1>
          <span className="hidden sm:inline text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/80 px-2 py-0.5 rounded-md">
            {t('common.devTools')}
          </span>
        </Link>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2">
        {user && (
          <Link
            to={panelPath('/credits')}
            className="inline-flex items-center gap-1.5 h-8 pl-2 pr-2.5 rounded-full border border-amber-200/80 dark:border-amber-500/25 bg-amber-50/80 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 text-sm font-medium tabular-nums hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors"
            title={t('credits.balance')}
          >
            <Coins className="h-3.5 w-3.5 shrink-0 opacity-80" />
            <span className="max-w-[4.5rem] truncate sm:max-w-none">{creditBalance}</span>
          </Link>
        )}

        {user ? (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 rounded-full pl-0.5 pr-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800"
              aria-label={t('header.accountMenu')}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-controls={menuId}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white text-xs font-semibold tracking-wide shadow-sm ring-2 ring-white dark:ring-gray-800">
                {getInitials(user.email)}
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                  menuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {menuOpen && (
              <div
                id={menuId}
                role="menu"
                className="absolute right-0 mt-2 w-[17.5rem] rounded-xl border border-gray-200 dark:border-gray-600/80 bg-white dark:bg-gray-800 shadow-xl shadow-gray-900/10 dark:shadow-black/40 z-50 overflow-hidden"
              >
                <div className="px-3.5 py-3 border-b border-gray-100 dark:border-gray-700/80">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white text-sm font-semibold">
                      {getInitials(user.email)}
                    </span>
                    <div className="min-w-0 flex-1 pt-0.5">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {user.email}
                      </p>
                      <span className="mt-1 inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-700/80 px-1.5 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300 capitalize">
                        {planLabel}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-1.5">
                  <Link
                    to={panelPath('/profile')}
                    role="menuitem"
                    onClick={closeAndNavigate}
                    className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors"
                  >
                    <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    {t('nav.profile')}
                  </Link>
                  <Link
                    to={panelPath('/credits')}
                    role="menuitem"
                    onClick={closeAndNavigate}
                    className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors"
                  >
                    <Coins className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="flex-1">{t('nav.credits')}</span>
                    <span className="tabular-nums text-xs font-medium text-gray-500 dark:text-gray-400">
                      {creditBalance}
                    </span>
                  </Link>
                </div>

                <div className="border-t border-gray-100 dark:border-gray-700/80 p-1.5">
                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {t('header.appearance')}
                  </p>
                  <div
                    className="mx-1 mb-1.5 grid grid-cols-2 gap-1 rounded-lg bg-gray-100 dark:bg-gray-900/60 p-1"
                    role="group"
                    aria-label={t('header.toggleTheme')}
                  >
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={!darkMode}
                      onClick={() => {
                        if (darkMode) onThemeToggle();
                      }}
                      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                        !darkMode
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      <Sun className="h-3.5 w-3.5" />
                      {t('header.themeLight')}
                    </button>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={darkMode}
                      onClick={() => {
                        if (!darkMode) onThemeToggle();
                      }}
                      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                        darkMode
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      <Moon className="h-3.5 w-3.5" />
                      {t('header.themeDark')}
                    </button>
                  </div>

                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {t('language.label')}
                  </p>
                  {SUPPORTED_LOCALES.map((code) => (
                    <button
                      key={code}
                      type="button"
                      role="menuitemradio"
                      aria-checked={effectiveLang === code}
                      onClick={() => setLanguage(code)}
                      className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-left transition-colors ${
                        effectiveLang === code
                          ? 'bg-primary-50 dark:bg-primary-900/25 text-primary-700 dark:text-primary-300 font-medium'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/80'
                      }`}
                    >
                      <span className="text-base leading-none" role="img" aria-hidden>
                        {LOCALE_FLAGS[code]}
                      </span>
                      <span className="flex-1">{t(`language.${code}`)}</span>
                      {effectiveLang === code && <Check className="h-3.5 w-3.5 shrink-0" />}
                    </button>
                  ))}
                </div>

                <div className="border-t border-gray-100 dark:border-gray-700/80 p-1.5">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleRestartOnboarding}
                    className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors"
                  >
                    <HelpCircle className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    {t('header.restartOnboarding')}
                  </button>
                </div>

                <div className="border-t border-gray-100 dark:border-gray-700/80 p-1.5">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    {t('header.logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onThemeToggle}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors"
            aria-label={t('header.toggleTheme')}
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;
